import { useNavigate } from 'react-router-dom'
import { faPlain } from 'utilities/helper/format'

interface Action {
    label: string
    to: string
    tone?: 'primary' | 'default' | 'alert'
    badge?: string
}

/**
 * What this person can do with this box, right now.
 *
 * Actions are filtered by role and by box state rather than shown-and-refused:
 * قرعه‌کشی only appears on an active box, پرداخت وام برندگان only while there
 * are unpaid winners, دریافت وام only when the caller actually has a cashout
 * waiting. A member sees the two that apply to them and nothing else.
 */
export default function CashboxActions({
    cashbox,
}: {
    cashbox: ICashboxDetail
}) {
    const navigate = useNavigate()
    const id = cashbox.id
    const isOwner = cashbox.membership?.role === 'own'
    const isActive = cashbox.state === 'act'
    const unpaidWinners = cashbox.last_cycle_unpaid_winners?.length ?? 0
    const drawn = Boolean(cashbox.cycle?.drawing_date_jalali)

    const actions: Array<Action> = []

    // An `unp` box cannot be used at all until its plan is settled, so that is
    // the only thing worth offering the manager until it is.
    if (isOwner && cashbox.state === 'unp')
        return (
            <div className="px-4 pb-4">
                <button
                    type="button"
                    onClick={() => navigate(`/cashbox/${id}/plans`)}
                    className="w-full h-12 rounded-[10px] bg-green-1 text-white text-sm font-bold active:scale-[0.99]"
                >
                    انتخاب نسخه‌ی صندوق
                </button>
            </div>
        )

    if (cashbox.membership?.have_cashout)
        actions.push({
            label: 'دریافت وام',
            to: `/cashbox/${id}/claim`,
            tone: 'primary',
        })

    if (isOwner) {
        if (unpaidWinners > 0)
            actions.push({
                label: 'پرداخت وام برندگان',
                to: `/cashbox/${id}/pay-winners`,
                tone: 'alert',
                badge: faPlain.format(unpaidWinners),
            })

        if (isActive && !drawn)
            actions.push({
                label: 'قرعه‌کشی',
                to: `/cashbox/${id}/draw`,
                tone: 'primary',
            })

        actions.push({ label: 'اعضا', to: `/cashbox/${id}/members` })

        if (isActive)
            actions.push({
                label: 'پرداخت گروهی',
                to: `/cashbox/${id}/group-pay`,
            })

        if (isActive && cashbox.manual_cashout)
            actions.push({
                label: 'پرداخت دستی وام',
                to: `/cashbox/${id}/manual-cashout`,
            })
    }

    actions.push({ label: 'تابلوی اعلانات', to: `/cashbox/${id}/bulletins` })

    if (isOwner) {
        actions.push({ label: 'نسخه‌ی صندوق', to: `/cashbox/${id}/plans` })
        actions.push({ label: 'تنظیمات', to: `/cashbox/${id}/settings` })
    }

    if (actions.length === 0) return null

    return (
        <div className="px-4 pb-4">
            <div className="grid grid-cols-2 gap-2">
                {actions.map((action) => (
                    <button
                        key={action.to + action.label}
                        type="button"
                        onClick={() => navigate(action.to)}
                        className={`h-12 rounded-[10px] text-sm active:scale-[0.99] flex items-center justify-center gap-1.5 ${
                            action.tone === 'primary'
                                ? 'bg-green-1 text-white font-bold'
                                : action.tone === 'alert'
                                ? 'bg-[#FFF8E6] border border-[#F0C36D] text-[#8A6100] font-bold'
                                : 'bg-white border border-divider text-text-body'
                        }`}
                    >
                        {action.label}
                        {action.badge && (
                            <span className="inline-flex items-center justify-center min-w-5 h-5 px-1 rounded-full bg-[#E84524] text-white text-xs">
                                {action.badge}
                            </span>
                        )}
                    </button>
                ))}
            </div>
        </div>
    )
}
